/**
 * cloudProjectsGroup.logic.ts — pure logic for the Cloud Projects sidebar group.
 *
 * Testable helpers:
 *  - deriveCloudProjectKey            source-scoped key (cloud:<runtimeId>)
 *  - deriveLocalProjectKey            source-scoped key (local:<logicalKey>)
 *  - partitionCloudAndLocalKeys       guarantees cloud ⊥ local, no collisions
 *  - runtimeStatusToDotVariant        ForgeRuntimeStatus → visual dot variant
 *  - resolveCloudGroupState           CmmdForgeRuntimeListSnapshot → CloudGroupState
 *  - resolveCloudRowResolvedRefs      derive the env store's ScopedProjectRefs for a runtimeId
 *  - toRegisteringCatalogRow          a row, reshaped for pickableProject.ts's shared matcher
 *
 * Project-import-job matching/expiry logic (resolveVisibleProjectImportJobs,
 * cloudProjectIsSuppressedByVisibleJob, resolveNextProjectImportExpiryAt) lives
 * in `cloudProjectImportJobs.logic.ts` — a separate concern with no shared
 * state or helpers with the identity/grouping logic below. Which pre-connect
 * catalog rows a surface offers is `components/projects/visibleCloudRows.ts`.
 *
 * No React imports — every function is a pure transform.
 */

import { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  CMMD_CLOUD_PROJECT_ENVIRONMENT_PREFIX,
  classifyEnvironmentId,
} from "../../environments/environmentIdClassification";
import type { RegisteringCatalogRow } from "../projects/pickableProject";
import type { CloudRuntimeProjectHandoff } from "./cloudRuntimeProjectHandoffStore";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import { buildPreConnectCloudSource } from "./preConnectCloudProjectSource";

/**
 * Owned by `environments/cmmdRuntimeEnvironmentId.ts`; re-exported because
 * `cmmdRuntimeInventoryProjection.ts` still imports the prefix through this
 * file.
 */
export { CMMD_RUNTIME_ENVIRONMENT_PREFIX } from "../../environments/cmmdRuntimeEnvironmentId";

export function collectCmmdRuntimeIdsFromProjects(
  projects: readonly SidebarProjectSnapshot[],
): ReadonlySet<string> {
  const runtimeIds = new Set<string>();
  for (const project of projects) {
    const environmentIds = [
      project.environmentId,
      ...(project.worktreeMembers ?? []).map((member) => member.environmentId),
      ...(project.memberProjectRefs ?? []).map((ref) => ref.environmentId),
    ];
    for (const environmentId of environmentIds) {
      const runtimeId = classifyEnvironmentId(environmentId).legacyRuntimeId;
      if (runtimeId !== null) runtimeIds.add(runtimeId);
    }
  }
  return runtimeIds;
}

/** Identity of a cloud project, independent of whether it has a runtime. */
export interface CloudProjectIdentity {
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
}

const CLOUD_RUNTIME_PROJECT_HANDOFF_MAX_AGE_MS = 5 * 60_000;

/**
 * Resolves runtime ids to stable CMMD project identity without inferring from
 * mutable repository or display-name fields.
 *
 * The live catalog supplies the normal runtime mapping. During a first connect
 * or recycle, the active connection flight can report the new runtime before
 * the catalog SSE row catches up. That transient handoff is accepted only when
 * it is fresh and the same catalog row still carries the runtime value observed
 * when the flight began, including the server-issued registration timestamp.
 * That timestamp is the row generation: unregistering and re-registering the
 * same CMMD project must not let a persisted handoff claim the replacement.
 */
export function buildCloudProjectIdentityByRuntimeId(
  catalogRows: readonly CloudProjectCatalogRow[],
  handoffs: readonly CloudRuntimeProjectHandoff[],
  nowMs = Date.now(),
): ReadonlyMap<string, CloudProjectIdentity> {
  const identityByProjectKey = new Map<string, CloudProjectIdentity>();
  const identityByRuntimeId = new Map<string, CloudProjectIdentity>();

  for (const row of catalogRows) {
    const identity = {
      cmmdOrganizationId: row.cmmdOrganizationId,
      cmmdProjectId: row.cmmdProjectId,
    };
    identityByProjectKey.set(deriveCloudProjectKey(identity), identity);
    if (row.runtimeId !== null) identityByRuntimeId.set(row.runtimeId, identity);
  }

  for (const handoff of handoffs) {
    if (nowMs - handoff.recordedAt > CLOUD_RUNTIME_PROJECT_HANDOFF_MAX_AGE_MS) continue;
    const catalogIdentity = identityByProjectKey.get(
      deriveCloudProjectKey({
        cmmdOrganizationId: handoff.cmmdOrganizationId,
        cmmdProjectId: handoff.cmmdProjectId,
      }),
    );
    const catalogRow = catalogRows.find(
      (row) =>
        row.cmmdOrganizationId === handoff.cmmdOrganizationId &&
        row.cmmdProjectId === handoff.cmmdProjectId,
    );
    if (
      catalogIdentity &&
      catalogRow?.runtimeId === handoff.previousCatalogRuntimeId &&
      catalogRow.registeredAt === handoff.catalogRegisteredAt &&
      !identityByRuntimeId.has(handoff.runtimeId)
    ) {
      identityByRuntimeId.set(handoff.runtimeId, catalogIdentity);
    }
  }

  return identityByRuntimeId;
}

/**
 * Environment id for a catalog row: the runtime's when one exists, otherwise the
 * project-scoped placeholder.
 */
export function resolveCloudProjectEnvironmentId(
  row: Pick<CloudProjectCatalogRow, "environmentId" | "cmmdOrganizationId" | "cmmdProjectId">,
): string {
  return (
    row.environmentId ??
    `${CMMD_CLOUD_PROJECT_ENVIRONMENT_PREFIX}${row.cmmdOrganizationId}:${row.cmmdProjectId}`
  );
}

/**
 * A catalog row, reshaped into the generic identity the two project pickers'
 * shared matcher (`pickableProject.ts`) answers "does this row register that
 * project" over.
 *
 * One function for both pickers (the sidebar's connected-project match and
 * the `/new` composer's draft match) so their adapters cannot drift the way
 * `resolveCloudProjectEnvironmentId`'s placeholder-environment-id handling
 * once had to be kept in sync by hand across two call sites.
 */
export function toRegisteringCatalogRow(row: CloudProjectCatalogRow): RegisteringCatalogRow {
  return {
    environmentId: resolveCloudProjectEnvironmentId(row),
    workspaceRoot: row.workspaceRoot,
    resolvedRef: row.resolvedRef,
  };
}

// ── Source-scoped keys ─────────────────────────────────────────────────────────

/**
 * Derive a source-scoped key for a cloud project row.
 *
 * Cloud keys are prefixed with `cloud:` so a project that exists in BOTH the
 * local store AND CMMD never collides on expansion-state, DnD SortableContext
 * ids, or thread routing.
 *
 * Keyed on the CMMD (organization, project) pair, NOT on the runtime id: the
 * runtime is created lazily on the first thread and destroyed/recreated over the
 * project's life, so a runtime-keyed identity would make the row appear to be a
 * different project every time its sprite changed — losing expansion state and
 * momentarily rendering two rows for one project.
 *
 * NOTE: Local rows intentionally keep their bare logical key (no `local:`
 * prefix) so persisted `projectOrder`/`projectExpandedById` state is not
 * invalidated. This is a conservative deviation from the spec's `local:` prefix.
 * See report notes.
 */
export function deriveCloudProjectKey(identity: CloudProjectIdentity): string {
  return `cloud:${identity.cmmdOrganizationId}:${identity.cmmdProjectId}`;
}

/**
 * The Registration a cloud project key was built from, or null.
 *
 * The inverse of {@link deriveCloudProjectKey}, and it lives beside it so one
 * file owns the format in both directions. A picker selection is a key, while
 * every lookup downstream is keyed by the Registration itself, and reading the
 * ids back out here is what keeps a second, drifting parser from appearing at
 * the call site.
 *
 * Null for every key in another namespace, which includes a connected
 * project's environment-scoped key.
 */
export function parseCloudProjectKey(key: string | null | undefined): CloudProjectIdentity | null {
  const match = /^cloud:(\d+):(\d+)$/.exec(key ?? "");
  if (!match) return null;
  return { cmmdOrganizationId: Number(match[1]), cmmdProjectId: Number(match[2]) };
}

/**
 * Last-resort key for a connected cloud runtime whose catalog row has not
 * arrived yet (first paint, or CMMD unreachable). Distinct namespace so it can
 * never be mistaken for — or collide with — a real project key; the row re-keys
 * itself once the catalog resolves.
 */
export function deriveCloudRuntimeFallbackKey(runtimeId: string): string {
  return `cloud-runtime:${runtimeId}`;
}

// ── Pre-connect cloud snapshot adapter ──────────────────────────────────────

/**
 * Sentinel ProjectId prefix for a not-yet-connected cloud row. A cloud project
 * has no VM-local ProjectId until its VM connects, so we mint a stable
 * placeholder from the CMMD project identity. ProjectId is just a non-empty
 * string (no UUID parsing anywhere), and the only consumers that treat `id` as a
 * real project id — workspace navigation + new-thread creation — are intercepted
 * by the pre-connect connect-first guard in useSidebarProjectItem, so this
 * sentinel never leaks into a route or an API call.
 */
export const PRE_CONNECT_CLOUD_PROJECT_ID_PREFIX = "cloud-unconnected:";

/**
 * Synthesize a SidebarProjectSnapshot for a not-yet-connected cloud catalog row
 * so it renders through the SAME SidebarProjectItem as connected/device projects
 * (chevron expands an empty thread list; thread hooks no-op on the absent env).
 * The `preConnectCloud` discriminant tells the shared row to connect-first on
 * action instead of navigating with the sentinel id.
 */
export function synthesizePreConnectCloudSnapshot(
  row: CloudProjectCatalogRow,
  // Once the VM connects, its env-state gains the VM-local project — a real
  // ProjectId distinct from this row's sentinel `id`. Threads are filed under
  // that resolved `(environmentId, projectId)`, so without surfacing it as a
  // member ref the cloud row renders "No chats" even with live threads in the
  // store. Callers pass a `resolveCloudRowResolvedRefs(...)` entry once connected.
  resolvedRef?: ScopedProjectRef | null,
  inventoryRefs: readonly ScopedProjectRef[] = [],
): SidebarProjectSnapshot {
  const projectKey = deriveCloudProjectKey(row);
  const snapshot: SidebarProjectSnapshot = {
    // Project base
    id: ProjectId.make(`${PRE_CONNECT_CLOUD_PROJECT_ID_PREFIX}${projectKey}`),
    environmentId: EnvironmentId.make(resolveCloudProjectEnvironmentId(row)),
    name: row.projectName,
    cwd: "",
    repositoryIdentity: null,
    availability: undefined,
    defaultModelSelection: null,
    createdAt: undefined,
    updatedAt: undefined,
    scripts: [],
    // Sidebar snapshot extras
    projectKey,
    environmentPresence: "remote-only",
    source: buildPreConnectCloudSource(row.runtimeId !== null),
    memberProjectRefs: resolvedRef ? [resolvedRef] : [],
    remoteEnvironmentLabels: [],
    worktreeMembers: [],
    activeWorktreeKey: null,
    preConnectCloud: {
      runtimeId: row.runtimeId,
      cmmdOrganizationId: row.cmmdOrganizationId,
      cmmdProjectId: row.cmmdProjectId,
      // FOR-32652. What makes the ensure name an Environment rather than let
      // CMMD infer one from the tuple. Null falls back to the legacy form.
      cmmdEnvironmentId: row.cmmdEnvironmentId,
      registeredAt: row.registeredAt,
      projectName: row.projectName,
      githubRepoFullName: row.githubRepoFullName,
      workspaceRoot: row.workspaceRoot,
      iconUrl: row.iconUrl,
      icon: row.icon,
      status: row.status,
      removal: row.removal,
    },
    cloudRemoval: row.removal,
  };
  return mergeSidebarProjectMemberRefs(snapshot, inventoryRefs);
}

export function mergeSidebarProjectMemberRefs(
  project: SidebarProjectSnapshot,
  additionalRefs: readonly ScopedProjectRef[],
): SidebarProjectSnapshot {
  if (additionalRefs.length === 0) return project;
  const memberProjectRefs = [...project.memberProjectRefs];
  const seen = new Set(memberProjectRefs.map((ref) => `${ref.environmentId}:${ref.projectId}`));
  for (const ref of additionalRefs) {
    const key = `${ref.environmentId}:${ref.projectId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    memberProjectRefs.push(ref);
  }
  return memberProjectRefs.length === project.memberProjectRefs.length
    ? project
    : { ...project, memberProjectRefs };
}

/**
 * Produce a local project key from the bare logical key.
 * This is a pass-through used to document intent; callers can use the bare
 * key directly and the result is the same (see note above on deviation).
 */
export function deriveLocalProjectKey(logicalKey: string): string {
  return logicalKey;
}

/**
 * Given a set of cloud project identities and a set of local logical project
 * keys, returns both sets of source-scoped keys and asserts they are disjoint.
 *
 * Cloud keys use `cloud:<org>:<project>` which cannot collide with local keys
 * (bare logical keys are either `env:uuid/path-hash` or a path-derived key,
 * never starting with `cloud:`).
 */
export function partitionCloudAndLocalKeys(input: {
  readonly cloudProjects: readonly CloudProjectIdentity[];
  readonly localLogicalKeys: readonly string[];
}): {
  readonly cloudKeys: readonly string[];
  readonly localKeys: readonly string[];
  readonly hasCollision: boolean;
} {
  const cloudKeys = input.cloudProjects.map((identity) => deriveCloudProjectKey(identity));
  const localKeys = input.localLogicalKeys.map(deriveLocalProjectKey);
  const cloudSet = new Set(cloudKeys);
  const hasCollision = localKeys.some((k) => cloudSet.has(k));
  return { cloudKeys, localKeys, hasCollision };
}
